import React, { useEffect, useState } from 'react';
import { useMutation, useReactiveVar, useSubscription } from '@apollo/client';
import AudioCaptionsLiveContainer from '/imports/ui/components/audio/audio-graphql/audio-captions/live/component';
import getFromUserSettings from '/imports/ui/services/users-settings';
import { useIsPresentationEnabled, useIsExternalVideoEnabled } from '/imports/ui/services/features';
import useCurrentUser from '/imports/ui/core/hooks/useCurrentUser';
import useMeeting from '/imports/ui/core/hooks/useMeeting';
import MeetGreetBanner from '/imports/ui/components/meet-greet-banner/component';
import { USER_AGGREGATE_COUNT_SUBSCRIPTION } from '/imports/ui/core/graphql/queries/users';
import {
  layoutSelect,
  layoutSelectInput,
  layoutSelectOutput,
} from '../layout/context';
import useSetSpeechOptions from '../audio/audio-graphql/hooks/useSetSpeechOptions';
import { handleIsNotificationEnabled } from '/imports/ui/components/plugins-engine/ui-commands/notification/handler';
import App from './component';
import { PINNED_PAD_SUBSCRIPTION } from '../notes/queries';
import useDeduplicatedSubscription from '../../core/hooks/useDeduplicatedSubscription';
import useSettings from '../../services/settings/hooks/useSettings';
import { SETTINGS } from '../../services/settings/enums';
import usePresentationSwap from '../../core/hooks/usePresentationSwap';
import { LAYOUT_TYPE } from '../layout/enums';
import { SET_PRESENTATION_FIT_TO_WIDTH } from './app-graphql/mutations';
import { CURRENT_PRESENTATION_PAGE_SUBSCRIPTION } from '../whiteboard/queries';
import { exportWhiteboardData, importWhiteboardData } from './service';
import Auth from '/imports/ui/services/auth';
import { courseSelect } from './context';
import LoadingScreen from '../common/loading-screen/component';
import { captureWhiteboardState, getStateSummary } from '../whiteboard/state-capture';
import autoScreenshareService from '/imports/ui/services/auto-screenshare';
import logger from '/imports/startup/client/logger';

const AppContainer = (props) => {
  const {
    viewScreenshare,
  } = useSettings(SETTINGS.DATA_SAVING);
  const { isNotificationEnabled } = useReactiveVar(handleIsNotificationEnabled);

  const {
    data: currentUser,
  } = useCurrentUser((u) => ({
    away: u.away,
    raiseHand: u.raiseHand,
    userId: u.userId,
    presenter: u.presenter,
    isModerator: u.isModerator,
  }));

  const {
    data: currentMeeting,
  } = useMeeting((m) => ({
    layout: m.layout,
    componentsFlags: m.componentsFlags,
  }));

  const { data: currentPageInfo } = useDeduplicatedSubscription(
    CURRENT_PRESENTATION_PAGE_SUBSCRIPTION,
    {
      // presenter can be undefinend leading to a bug
      // eslint-disable-next-line no-unneeded-ternary
      skip: currentUser?.presenter ? false : true,
    },
  );

  const presentationRestoreOnUpdate = getFromUserSettings(
    'bbb_force_restore_presentation_on_new_events',
    window.meetingClientSettings.public.presentation.restoreOnUpdate,
  );

  const NOTES_CONFIG = window.meetingClientSettings.public.notes;

  const {
    darkTheme,
  } = useSettings(SETTINGS.APPLICATION);

  const { partialUtterances, minUtteranceLength } = useSettings(SETTINGS.TRANSCRIPTION);

  const genericMainContent = layoutSelectInput((i) => i.genericMainContent);
  const captionsStyle = layoutSelectOutput((i) => i.captions);
  const presentation = layoutSelectInput((i) => i.presentation);
  const sharedNotesInput = layoutSelectInput((i) => i.sharedNotes);
  const { hideNotificationToasts } = layoutSelectInput((i) => i.notificationsBar);
  const layoutType = layoutSelect((i) => i.layoutType);
  const isNonMediaLayout = [
    LAYOUT_TYPE.CAMERAS_ONLY,
    LAYOUT_TYPE.PARTICIPANTS_AND_CHAT_ONLY,
  ].includes(layoutType);
  const setSpeechOptions = useSetSpeechOptions();
  const { data: pinnedPadData } = useDeduplicatedSubscription(PINNED_PAD_SUBSCRIPTION);
  const isSharedNotesPinnedFromGraphql = !!pinnedPadData
    && pinnedPadData.sharedNotes[0]?.sharedNotesExtId === NOTES_CONFIG.id;
  const isSharedNotesPinned = sharedNotesInput?.isPinned && isSharedNotesPinnedFromGraphql;
  const isExternalVideoEnabled = useIsExternalVideoEnabled();
  const isPresentationEnabled = useIsPresentationEnabled();
  const [showScreenshare,swapPresentation] = usePresentationSwap();
  const [setPresentationFitToWidth] = useMutation(SET_PRESENTATION_FIT_TO_WIDTH);

  const isPresenter = currentUser?.presenter;

  const { isOpen } = presentation;
  const presentationIsOpen = isOpen;

  const isSharingVideo = currentMeeting?.componentsFlags.hasExternalVideo;

  const shouldShowExternalVideo = isExternalVideoEnabled && isSharingVideo;

  const shouldShowGenericMainContent = !!genericMainContent.genericContentId;
  // Check if auto-screenshare is enabled
  const isAutoScreenshareEnabled = autoScreenshareService.isEnabled();

  const shouldShowScreenshare = (viewScreenshare || isPresenter)
    && (currentMeeting?.componentsFlags?.hasScreenshare
      || currentMeeting?.componentsFlags?.hasCameraAsContent) &&
  showScreenshare;
      
    
  
  const shouldShowPresentation = (!shouldShowScreenshare && !isSharedNotesPinned
    && !shouldShowExternalVideo && !shouldShowGenericMainContent
    && (presentationIsOpen || presentationRestoreOnUpdate)) && isPresentationEnabled;



  const currentPageInfoData = currentPageInfo?.pres_page_curr[0] ?? {};
  // const fitToWidth = currentPageInfoData?.fitToWidth ?? true;
  const fitToWidth = true
  const pageId = currentPageInfoData?.pageId ?? '';
  const courseDetails = courseSelect((state) => state);
  const [loading, setLoading] = useState(false)

  // Subscribe to user count for Meet & Greet "tutor will enter shortly" banner
  const { data: userCountData } = useSubscription(USER_AGGREGATE_COUNT_SUBSCRIPTION);
  const userCount = userCountData?.user_aggregate?.aggregate?.count ?? 0;

  // Check if this is a Meet & Greet session and should show the waiting banner
  const isMeetGreet = courseDetails?.course?.isMeetGreet ?? false;
  const userRole = courseDetails?.course?.userRole;
  const showMeetGreetBanner = isMeetGreet && userRole === 'GUEST' && userCount === 1;
  const handlePresentationFitToWidth = (ftw) => {
    // setPresentationFitToWidth({
    //   variables: {
    //     pageId,
    //     fitToWidth: ftw,
    //   },
    // });
  };

  useEffect(()=>{
    if (isPresenter && currentMeeting?.componentsFlags?.hasScreenshare){
       swapPresentation(false);
    }
  },[currentMeeting?.componentsFlags?.hasScreenshare, isPresenter])
  // Update after editing app savings
  useEffect(() => {
    setSpeechOptions(
      partialUtterances,
      minUtteranceLength,
    );
  }, [partialUtterances, minUtteranceLength]);


  // useEffect(() => {
  //   if (currentUser?.isModerator && courseDetails?.course) {
  //     setLoading(true)
  //     const importApiPayload = {
  //       newMeetingId: Auth?._meetingID,
  //       userId: Auth?._userID,
  //       courseId: courseDetails?.course?.courseDetail?.courseSessionId
  //     }
  //     importWhiteboardData(importApiPayload).then((importRes) => {
  //     }).catch((error) => {
  //       console.log({ error })
  //     }).finally(() => {
  //       setLoading(false)
  //     });
  //   }

  // }, [currentUser, courseDetails?.course])

  useEffect(() => {
    const meetingId = Auth?._meetingID;
    const course = courseDetails?.course?.courseDetail;

    const hasImportedKey = `hasImported-${meetingId}`;

    // Check if meeting ID exists, user is moderator, course exists, and not yet imported
    if (
      currentUser?.isModerator &&
      course &&
      meetingId &&
      localStorage.getItem(hasImportedKey) !== 'true'
    ) {
      setLoading(true);

      // Use standard BBB payload structure (see docs/BBB_DATA_CONTRACTS.md)
      const importApiPayload = {
        internalMeetingID: meetingId,           // Standard field name (capital ID)
        externalUserID: Auth?._externUserID,    // Our UUID, not BBB's internal user ID
        sessionId: course.courseSessionId,      // Lesson/session ID for lookup
      };

      importWhiteboardData(importApiPayload)
        .then((response) => {
          localStorage.setItem(hasImportedKey, 'true'); // Mark as imported

          // Get the imported state from the response
          const importedState = response?.data?.data?.state;

          if (!importedState || !importedState.slides) {
            logger.info({ logCode: 'whiteboard_import_no_state' }, '[Import] No state to restore');
            return;
          }

          // Count annotations to restore
          const totalAnnotations = importedState.slides.reduce(
            (sum, slide) => sum + (slide.annotations?.length || 0), 0
          );
          logger.info({ logCode: 'whiteboard_import_restoring', extraInfo: { totalAnnotations, slideCount: importedState.slides.length } }, `[Import] Restoring ${totalAnnotations} annotations from ${importedState.slides.length} slides`);

          if (totalAnnotations === 0) {
            logger.info({ logCode: 'whiteboard_import_no_annotations' }, '[Import] No annotations to restore');
            return;
          }

          // Wait for tldraw editor to be ready, then restore annotations
          const restoreAnnotations = () => {
            const editor = window.tlEditorRef?.current;
            if (!editor) {
              logger.debug({ logCode: 'whiteboard_import_editor_not_ready' }, '[Import] Editor not ready, retrying in 500ms...');
              setTimeout(restoreAnnotations, 500);
              return;
            }

            try {
              // Restore annotations for each slide
              importedState.slides.forEach((slide) => {
                if (!slide.annotations || slide.annotations.length === 0) return;

                // Find or create the page for this slide
                const pageId = slide.slideId || `page:${slide.slideNumber}`;

                slide.annotations.forEach((annotation) => {
                  try {
                    // Reconstruct the shape from the annotation data
                    const shape = {
                      id: annotation.annotationId,
                      type: annotation.type,
                      parentId: pageId,
                      x: annotation.data?.x || 0,
                      y: annotation.data?.y || 0,
                      rotation: annotation.data?.rotation || 0,
                      props: { ...annotation.data },
                      meta: {
                        createdBy: annotation.userId,
                        createdAt: annotation.createdAt,
                      },
                    };

                    // Remove position data from props (already on shape)
                    delete shape.props.x;
                    delete shape.props.y;
                    delete shape.props.rotation;

                    editor.createShape(shape);
                  } catch (shapeError) {
                    logger.error({ logCode: 'whiteboard_import_shape_error', extraInfo: { error: shapeError?.message, annotationId: annotation?.annotationId } }, '[Import] Error creating shape');
                  }
                });
              });

              logger.info({ logCode: 'whiteboard_import_complete' }, '[Import] Annotation restoration complete');
            } catch (restoreError) {
              logger.error({ logCode: 'whiteboard_import_restore_error', extraInfo: { error: restoreError?.message } }, '[Import] Error restoring annotations');
            }
          };

          // Start restoration after a delay to ensure editor is mounted
          setTimeout(restoreAnnotations, 1000);
        })
        .catch((error) => {
          logger.error({ logCode: 'whiteboard_import_fetch_error', extraInfo: { error: error?.message } }, '[Import] Error fetching whiteboard state');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [currentUser?.isModerator, Auth?._meetingID, courseDetails?.course?.courseDetail]);


  useEffect(() => {
    const meetingId = Auth?._meetingID;
    const courseId = courseDetails?.course?.courseDetail?.courseId;
    const sessionId = courseDetails?.course?.courseDetail?.courseSessionId;
    const presentationId = courseDetails?.course?.courseDetail?.presentationId;

    if (!meetingId || !courseId || !currentUser?.isModerator) return;

    const intervalId = setInterval(() => {
      // Capture actual whiteboard state from tldraw editor
      const editor = window.tlEditorRef?.current;
      // Get current slide directly from editor to avoid dependency on currentPageInfo
      const currentSlide = editor?.getCurrentPageId ?
        parseInt(editor.getCurrentPageId().replace('page:', ''), 10) || 1 : 1;

      const whiteboardState = captureWhiteboardState(
        editor,
        presentationId,
        currentSlide
      );

      const exportApiPayload = {
        internalMeetingID: meetingId,
        externalUserID: Auth?._externUserID,
        sessionId,
        state: whiteboardState || {},
      };

      logger.info({
        logCode: 'whiteboard_export_periodic',
        extraInfo: { summary: getStateSummary(whiteboardState) },
      }, 'Periodic whiteboard export');

      exportWhiteboardData(exportApiPayload)
        .then(() => {
          logger.info({
            logCode: 'whiteboard_export_periodic_success',
          }, 'Periodic whiteboard export completed');
        })
        .catch((error) => {
          logger.error({
            logCode: 'whiteboard_export_periodic_failed',
            extraInfo: { error: error?.message },
          }, 'Periodic whiteboard export failed');
        });
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(intervalId);
  }, [Auth?._meetingID, courseDetails?.course?.courseDetail?.courseSessionId, courseDetails?.course?.courseDetail?.presentationId, currentUser?.isModerator]);

  if (!currentUser) {
    return null;
  }
  if (loading) return <LoadingScreen />;

  return currentUser?.userId
    ? (
      <>
        {showMeetGreetBanner && <MeetGreetBanner />}
        <App
          {...{
            fitToWidth,
            handlePresentationFitToWidth,
            hideActionsBar: getFromUserSettings('bbb_hide_actions_bar', false)
              || getFromUserSettings('bbb_hide_controls', false),
            isNonMediaLayout,
            currentUserAway: currentUser.away,
            currentUserRaiseHand: currentUser.raiseHand,
            captionsStyle,
            presentationIsOpen,
            shouldShowExternalVideo,
            shouldShowScreenshare,
            isSharedNotesPinned,
            shouldShowPresentation,
            isNotificationEnabled,
            genericMainContentId: genericMainContent.genericContentId,
            audioCaptions: <AudioCaptionsLiveContainer />,
            hideNotificationToasts: hideNotificationToasts
              || getFromUserSettings('bbb_hide_notifications', false),
            darkTheme,
            isAutoScreenshareEnabled,
            isPresenter
          }}
          {...props}
        />
      </>
    )
    : null;
};

export default AppContainer;
